import {
	getAccountDetails,
	getOwnerBadge,
	getValidationOutcome,
	getValidationOutcomeColor,
	VALIDATION_OUTCOME_BADGE_COLORS,
	VALIDATION_OUTCOME_LABEL_KEYS,
	VALIDATION_OUTCOME_ORDER,
	VERIFICATION_BUCKET_LABEL_KEYS,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/components/BankAccounts.utils.ts';
import { CountDot } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/components/CountDot.tsx';
import { DataGapIndicator } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/components/DataGapIndicator.tsx';
import { RiskCell } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/components/RiskCell.tsx';
import { FooterSummaryCell } from '@dealstack/frontend/shared/components/Table/MantineTable/Cells/FooterSummaryCell.tsx';
import {
	CellContent,
	SingleLineText,
	singleLineTextStyle,
} from '@dealstack/frontend/shared/components/Table/MantineTable/Cells/cell.styles.ts';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import {
	BANK_ACCOUNT_VERIFICATION_BUCKET_ORDER,
	getBankAccountVerificationBucket,
	getBankAccountVerificationBucketColor,
} from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import {
	DataMipInvestorBankAccountRes,
	InvestorBankAccountsStatsRes,
} from '@dealstack/shared/lib/generated';
import { Badge, Box, Flex, Text } from '@mantine/core';
import { useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';

interface Props {
	stats?: InvestorBankAccountsStatsRes;
}

export const useBankAccountsTable = ({ stats }: Props) => {
	const tMipManage = useTranslationMipManage();

	// Counts span the whole filtered set, not just this page.
	const total = stats?.total ?? 0;

	const columns = useMemo(
		() => [
			createTextColumn<DataMipInvestorBankAccountRes>({
				id: 'accountHolder',
				headerText: tMipManage('bank_accounts.table.account_holder'),
				columnLabel: tMipManage('bank_accounts.table.account_holder'),
				accessorFn: (row) => row.accountHolder,
				minSize: 220,
				meta: { widthPercentage: 22 },
				enableSorting: false,
				enableGlobalFilter: true,
				showFilter: true,
				filterText: tMipManage('bank_accounts.table.account_holder.filter'),
				filterVariant: 'text',
				cell: ({ row }) => (
					<CellContent>
						<Text
							size="sm"
							fw={500}
							truncate
							component={SingleLineText}
							style={singleLineTextStyle}
						>
							{row.original.accountHolder || LONG_DASH}
						</Text>
					</CellContent>
				),
				showFooter: true,
				footer: () => (
					<FooterSummaryCell
						label={tMipManage('bank_accounts.footer.number_of_records')}
						value={total}
					/>
				),
			}),
			createTextColumn<DataMipInvestorBankAccountRes>({
				id: 'accountDetails',
				headerText: tMipManage('bank_accounts.table.account_details'),
				columnLabel: tMipManage('bank_accounts.table.account_details'),
				accessorFn: (row) => getAccountDetails(row).line1,
				minSize: 240,
				meta: { widthPercentage: 26 },
				enableSorting: false,
				enableGlobalFilter: true,
				cell: ({ row }) => {
					const details = getAccountDetails(row.original);
					const countryCode = row.original.countryCode ?? '';
					const countryName = getCountryNameByCode(countryCode);

					return (
						<CellContent>
							<Flex align="center" gap="0.375rem" miw={0}>
								<Text size="sm" truncate style={{ minWidth: 0 }}>
									{details.line1}
								</Text>
								{countryName && (
									<ReactCountryFlag
										countryCode={countryCode}
										svg
										title={countryName}
										aria-label={countryName}
									/>
								)}
							</Flex>
							{details.detailLines.map((line) => (
								<Text
									key={line}
									size="sm"
									c="dark.0"
									truncate
									component={SingleLineText}
								>
									{line}
								</Text>
							))}
						</CellContent>
					);
				},
				showFooter: true,
				footer: () => (
					<FooterSummaryCell
						label={tMipManage('bank_accounts.footer.number_of_records')}
						value={total}
					/>
				),
			}),
			createTextColumn<DataMipInvestorBankAccountRes>({
				id: 'validation',
				headerText: tMipManage('bank_accounts.table.validation'),
				columnLabel: tMipManage('bank_accounts.table.validation'),
				accessorFn: (row) => getValidationOutcome(row),
				minSize: 140,
				meta: { widthPercentage: 12 },
				// Derived from the newest validation log rather than a column.
				enableSorting: false,
				enableGlobalFilter: false,
				showFilter: true,
				filterText: tMipManage('bank_accounts.table.validation.filter'),
				filterVariant: 'multi-select',
				mantineFilterMultiSelectProps: {
					data: VALIDATION_OUTCOME_ORDER.map((outcome) => ({
						value: outcome,
						label: tMipManage(VALIDATION_OUTCOME_LABEL_KEYS[outcome]),
					})),
				},
				cell: ({ row }) => {
					const outcome = getValidationOutcome(row.original);

					return (
						<Badge
							variant="outline"
							size="sm"
							fw={400}
							color={VALIDATION_OUTCOME_BADGE_COLORS[outcome]}
						>
							{tMipManage(VALIDATION_OUTCOME_LABEL_KEYS[outcome])}
						</Badge>
					);
				},
				showFooter: true,
				footer: () => (
					<Flex h="100%" w="100%" align="center" gap={8}>
						{VALIDATION_OUTCOME_ORDER.map((outcome) => (
							<CountDot
								key={outcome}
								color={getValidationOutcomeColor(outcome)}
								value={stats?.byValidationOutcome?.[outcome] ?? 0}
							/>
						))}
					</Flex>
				),
			}),
			createTextColumn<DataMipInvestorBankAccountRes>({
				id: 'risk',
				headerText: tMipManage('bank_accounts.table.risk'),
				columnLabel: tMipManage('bank_accounts.table.risk'),
				accessorFn: (row) => row.riskCount,
				minSize: 120,
				meta: { widthPercentage: 8 },
				enableSorting: false,
				enableGlobalFilter: false,
				cell: ({ row }) => (
					<RiskCell bankAccountId={row.original.id} riskCount={row.original.riskCount} />
				),
				showFooter: true,
				footer: () => (
					<Flex h="100%" w="100%" align="center" gap={8}>
						<CountDot
							color={defaultTheme.v3.colors.dark[0]}
							value={stats?.noRiskCount ?? 0}
						/>
						<CountDot
							color={defaultTheme.v3.colors.red[8]}
							value={stats?.riskCount ?? 0}
						/>
					</Flex>
				),
			}),
			createTextColumn<DataMipInvestorBankAccountRes>({
				id: 'verificationStatus',
				headerText: tMipManage('bank_accounts.table.verification_status'),
				columnLabel: tMipManage('bank_accounts.table.verification_status'),
				accessorFn: (row) => getBankAccountVerificationBucket(row.status) ?? '',
				minSize: 160,
				meta: { widthPercentage: 14 },
				enableSorting: true,
				enableGlobalFilter: false,
				showFilter: true,
				filterText: tMipManage('bank_accounts.table.verification_status.filter'),
				filterVariant: 'multi-select',
				mantineFilterMultiSelectProps: {
					data: BANK_ACCOUNT_VERIFICATION_BUCKET_ORDER.map((bucket) => ({
						value: bucket,
						label: tMipManage(VERIFICATION_BUCKET_LABEL_KEYS[bucket]),
					})),
				},
				cell: ({ row }) => {
					const bucket = getBankAccountVerificationBucket(row.original.status);

					if (!bucket) {
						return <Text size="sm">{LONG_DASH}</Text>;
					}

					return (
						<Flex align="center" gap={6}>
							<Box
								w={6}
								h={6}
								style={{
									borderRadius: '50%',
									backgroundColor: getBankAccountVerificationBucketColor(bucket),
								}}
							/>
							<Text size="sm" c={getBankAccountVerificationBucketColor(bucket)}>
								{tMipManage(VERIFICATION_BUCKET_LABEL_KEYS[bucket])}
							</Text>
						</Flex>
					);
				},
				showFooter: true,
				footer: () => (
					<Flex h="100%" w="100%" align="center" gap={8}>
						{BANK_ACCOUNT_VERIFICATION_BUCKET_ORDER.map((bucket) => (
							<CountDot
								key={bucket}
								color={getBankAccountVerificationBucketColor(bucket)}
								value={stats?.byVerificationStatus?.[bucket] ?? 0}
							/>
						))}
					</Flex>
				),
			}),
			createTextColumn<DataMipInvestorBankAccountRes>({
				id: 'ownerName',
				headerText: tMipManage('bank_accounts.table.owner'),
				columnLabel: tMipManage('bank_accounts.table.owner'),
				accessorFn: (row) => row.ownerName,
				minSize: 200,
				meta: { widthPercentage: 12 },
				enableSorting: true,
				enableGlobalFilter: true,
				showFilter: true,
				filterText: tMipManage('bank_accounts.table.owner.filter'),
				filterVariant: 'text',
				cell: ({ row }) => {
					// Neither the programme nor a vehicle is a natural person, so the badge
					// says which kind of owner this is rather than leaving a company name
					// looking like someone's.
					const ownerBadge = getOwnerBadge(row.original, tMipManage);

					return (
						<CellContent>
							<Flex align="center" gap="0.375rem" miw={0}>
								<Text
									size="sm"
									component={SingleLineText}
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '0.25rem',
										...(ownerBadge ? { lineHeight: 1.7 } : {}),
									}}
								>
									{row.original.ownerName || LONG_DASH}{' '}
									{ownerBadge && (
										<Badge
											variant="outline"
											size="sm"
											fw={400}
											color={ownerBadge.color}
											c={ownerBadge.textColor}
										>
											{ownerBadge.label}
										</Badge>
									)}
									<DataGapIndicator
										bankAccountId={row.original.id}
										dataGaps={row.original.dataGaps}
									/>
								</Text>
							</Flex>
							{!ownerBadge && (
								<Text
									size="sm"
									c="dark.0"
									truncate
									component={SingleLineText}
									style={singleLineTextStyle}
								>
									{row.original.ownerEmail || LONG_DASH}
								</Text>
							)}
						</CellContent>
					);
				},
				showFooter: true,
				footer: () => (
					<FooterSummaryCell
						label={tMipManage('bank_accounts.footer.number_of_records')}
						value={stats?.ownerCount ?? 0}
					/>
				),
			}),
			createTextColumn<DataMipInvestorBankAccountRes>({
				id: 'updatedAt',
				headerText: tMipManage('bank_accounts.table.last_updated'),
				columnLabel: tMipManage('bank_accounts.table.last_updated'),
				accessorFn: (row) => row.updatedAt,
				minSize: 140,
				meta: { widthPercentage: 6 },
				enableSorting: true,
				enableGlobalFilter: false,
				cell: ({ row }) => (
					<Text size="sm">
						{row.original.updatedAt
							? formatStringDateToUI(
									row.original.updatedAt,
									DATE_FORMATS.CALENDAR_DATE
								)
							: LONG_DASH}
					</Text>
				),
				showFooter: true,
				footer: () => (
					<FooterSummaryCell
						label={tMipManage('bank_accounts.footer.number_of_records')}
						value={total}
					/>
				),
			}),
		],
		[stats, total, tMipManage]
	);

	return { columns };
};
